import { computed, inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, take } from 'rxjs';
import { AnalysisService } from '@core/analysis/services/analysis.service';
import { RefactorAnalysisService } from '@app/features/analysis-refactor/core/services/refactor-analysis.service';
import { LandService } from '@app/features/preview/pages/hbu-study/core/services/land.service';
import { parsePreviewLandId } from '@app/features/preview/core/utils/preview-chart.util';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable()
export class PreviewContextService {
  private readonly route = inject(ActivatedRoute);
  private readonly landService = inject(LandService);
  private readonly analysisService = inject(AnalysisService);
  private readonly refactorAnalysisService = inject(RefactorAnalysisService);
  private http = inject(HttpClient)

  readonly region = signal('Riyadh');
  readonly landId = signal(0);
  readonly currentDate = computed(() => new Date().toLocaleDateString('en-GB'));

  initFromRoute(options?: {
    useParentRoute?: boolean;
    withDemographic?: boolean;
    useLandService?: boolean;
    onLoaded?: () => void;
  }): void {
    const route = options?.useParentRoute !== false && this.route.parent ? this.route.parent : this.route;

    route.paramMap.pipe(take(1)).subscribe((params) => {
      const region = params.get('region') || 'Riyadh';
      const landId = parsePreviewLandId(params.get('landId'));
      this.region.set(region);
      this.landId.set(landId);

      if (!landId) {
        options?.onLoaded?.();
        return;
      }

      if (options?.useLandService) {
        this.landService.getLandData(region, landId, !!options.withDemographic, options.onLoaded ?? null);
        return;
      }

      this.ensureLandSelected(region, landId, options?.onLoaded);
    });
  }

  initFromInputs(region: string, landId: number | string, options?: {
    withDemographic?: boolean;
    onLoaded?: () => void;
  }): void {
    const parsedLandId = parsePreviewLandId(landId);
    this.region.set(region || 'Riyadh');
    this.landId.set(parsedLandId);

    if (!parsedLandId) {
      options?.onLoaded?.();
      return;
    }

    this.landService.getLandData(
      this.region(),
      parsedLandId,
      !!options?.withDemographic,
      options?.onLoaded ?? null
    );
  }

  ensureLandSelected(region: string, landId: number, onLoaded?: () => void): void {
    this.refactorAnalysisService
      .getLandData(landId)
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          const landName =
            response.success && response.data?.name ? response.data.name : `Land ${landId}`;
          this.analysisService.setCurrentSelection(region, landId, landName);
          onLoaded?.();
        },
        error: () => {
          this.analysisService.setCurrentSelection(region, landId, `Land ${landId}`);
          onLoaded?.();
        },
      });
  }

  ensureAnalysisDataLoaded(onLoaded?: () => void, onError?: () => void): void {
    this.analysisService
      .ensureDataLoadedForCurrentLand()
      .pipe(take(1))
      .subscribe({
        next: () => onLoaded?.(),
        error: () => onError?.(),
      });
  }

  loadLandData(landId:number){
    return this.http.get<any>(`${environment.baseUrl}/admin/lands/${landId}`);
  }

  //dashboard charts
  getChartsDataBySubSectionId(regionId: number,subSectionId: number): Observable<any> {
    return this.http.get<any>(`${environment.baseUrl}/admin/charts/website/by-region-and-sub-section?region_id=${regionId}&charts_sub_section_id=${subSectionId}`);
  }

  //website charts
  getWebsiteChartsDataByLandId(landId:number) {
    return this.http.get<any>(`${environment.baseUrl}/admin/lands/${landId}/charts/website`)
  }
}
